/* ──────────────────────────────────────────────────────────
   animation.js  -  GSAP + ScrollTrigger + Splitting.js setup
   ────────────────────────────────────────────────────────── */

import { gsap, ScrollTrigger } from './gsap-setup.js'
import Splitting from 'splitting'

const prefersReducedMotion = window.matchMedia(
	'(prefers-reduced-motion: reduce)',
).matches

/* Elements are marked once processed so a re-init (after an htmx/Sprig swap)
   only touches freshly-injected content and never re-hides what's already
   revealed. `immediate` reveals in place without a ScrollTrigger — swapped-in
   content (e.g. Stories "load more") is already in the viewport, so waiting
   for a scroll enter would leave it stuck at the FOUC-guard opacity:0. */
function revealAll(root) {
	root
		.querySelectorAll(
			'.animate-reveal, .animate-reveal-cascade, .animate-words-up, .reveal-image',
		)
		.forEach((el) => {
			gsap.set(el, { autoAlpha: 1, y: 0, yPercent: 0, scale: 1 })
			el.dataset.revealDone = ''
		})
	root.querySelectorAll('.animate-reveal-cascade').forEach((parent) => {
		gsap.set(Array.from(parent.children), { autoAlpha: 1, y: 0 })
	})
	root
		.querySelectorAll('[data-tl-line]')
		.forEach((line) => gsap.set(line, { scaleX: 1, scaleY: 1 }))
}

function initReveals(root, { immediate = false } = {}) {
	if (prefersReducedMotion || immediate) {
		revealAll(root)
		return
	}

	/* ───────────────────────────────
       1. Fade-in reveal elements
     ─────────────────────────────── */
	const revealElements = root.querySelectorAll(
		'.animate-reveal:not([data-reveal-done])',
	)

	if (revealElements.length > 0) {
		gsap.set(revealElements, { autoAlpha: 0, y: 16 })
		revealElements.forEach((el) => (el.dataset.revealDone = ''))

		ScrollTrigger.batch(revealElements, {
			batchMax: 10,
			onEnter: (els) => {
				gsap.to(els, {
					autoAlpha: 1,
					y: 0,
					duration: 0.7,
					ease: 'power3.out',
					clearProps: 'transform',
				})
			},
			start: (batch) =>
				batch.trigger.getBoundingClientRect().top + window.scrollY >
				window.innerHeight
					? 'top 85%'
					: 'top bottom',
			once: true,
		})
	}

	/* ───────────────────────────────
       2. Cascade reveal (parent staggers its children)
       Add `.animate-reveal-cascade` to a parent (grid, ol, ul). Its direct
       children fade-up with a 60ms stagger when the parent enters the
       viewport.
     ─────────────────────────────── */
	const cascadeParents = root.querySelectorAll(
		'.animate-reveal-cascade:not([data-reveal-done])',
	)

	cascadeParents.forEach((parent) => {
		const children = Array.from(parent.children)
		if (children.length === 0) return

		parent.dataset.revealDone = ''
		gsap.set(children, { autoAlpha: 0, y: 20 })
		// Suppress any CSS transform-transition (e.g. card hover-lift) so it
		// can't fight GSAP's reveal and cause a flicker; restore it after.
		children.forEach((c) => {
			c.style.transition = 'none'
		})

		ScrollTrigger.create({
			trigger: parent,
			start:
				parent.getBoundingClientRect().top + window.scrollY >
				window.innerHeight
					? 'top 85%'
					: 'top bottom',
			once: true,
			onEnter: () => {
				gsap.to(children, {
					autoAlpha: 1,
					y: 0,
					duration: 0.65,
					ease: 'power3.out',
					stagger: 0.06,
					clearProps: 'transform',
					onComplete: () => {
						children.forEach((c) => {
							c.style.transition = ''
						})
					},
				})
			},
		})
	})

	/* ───────────────────────────────
       3. Words slide up on scroll
     ─────────────────────────────── */
	const wordsUpElements = root.querySelectorAll(
		'.animate-words-up:not([data-reveal-done])',
	)

	if (wordsUpElements.length > 0) {
		const splitResults = Splitting({ target: wordsUpElements, by: 'words' })

		splitResults.forEach(({ words, el }) => {
			if (!words || words.length === 0) return

			el.dataset.revealDone = ''

			// The container is hidden up-front by CSS (reveal FOUC guard);
			// reveal it now that the words carry their own hidden state.
			gsap.set(el, { autoAlpha: 1 })
			gsap.set(words, { yPercent: 100, autoAlpha: 0 })

			ScrollTrigger.batch(words, {
				batchMax: 15,
				onEnter: (els) => {
					gsap.to(els, {
						yPercent: 0,
						autoAlpha: 1,
						stagger: 0.05,
						duration: 0.6,
						ease: 'power3.out',
						clearProps: 'all',
					})
				},
				start:
					el.getBoundingClientRect().top + window.scrollY >
					window.innerHeight
						? 'top 85%'
						: 'top bottom',
				once: true,
			})
		})
	}

	/* ───────────────────────────────
       4. Image scale-fade reveal
       Add `.reveal-image` to a figure/wrapper. Image fades from 0 with
       a subtle 1.04→1 scale settling over 900ms.
     ─────────────────────────────── */
	const imageReveals = root.querySelectorAll(
		'.reveal-image:not([data-reveal-done])',
	)

	if (imageReveals.length > 0) {
		gsap.set(imageReveals, { autoAlpha: 0, scale: 1.04 })
		imageReveals.forEach((el) => (el.dataset.revealDone = ''))

		ScrollTrigger.batch(imageReveals, {
			batchMax: 8,
			onEnter: (els) => {
				gsap.to(els, {
					autoAlpha: 1,
					scale: 1,
					duration: 0.9,
					ease: 'power3.out',
					clearProps: 'transform',
				})
			},
			start: (batch) =>
				batch.trigger.getBoundingClientRect().top + window.scrollY >
				window.innerHeight
					? 'top 85%'
					: 'top bottom',
			once: true,
		})
	}

	/* ───────────────────────────────
       5. Timeline line draw
       Each `[data-timeline]` root grows its `[data-tl-line]` connectors in
       (scaleY for vertical spines, scaleX for horizontal tracks) when the
       timeline enters view. Dots + content fade in via `.animate-reveal-cascade`.
     ─────────────────────────────── */
	root
		.querySelectorAll('[data-timeline]:not([data-reveal-done])')
		.forEach((tlRoot) => {
			const lines = tlRoot.querySelectorAll('[data-tl-line]')
			if (lines.length === 0) return

			tlRoot.dataset.revealDone = ''
			lines.forEach((line) => {
				gsap.set(
					line,
					line.dataset.tlLine === 'x' ? { scaleX: 0 } : { scaleY: 0 },
				)
			})

			ScrollTrigger.create({
				trigger: tlRoot,
				start:
					tlRoot.getBoundingClientRect().top + window.scrollY >
					window.innerHeight
						? 'top 80%'
						: 'top bottom',
				once: true,
				onEnter: () => {
					gsap.to(lines, {
						scaleX: 1,
						scaleY: 1,
						duration: 0.8,
						ease: 'power3.out',
						stagger: 0.06,
					})
				},
			})
		})
}

/* Story ids revealed so far, tracked across Sprig swaps so "load more" only
   animates the freshly-appended stories and leaves the ones already on screen
   untouched. Ids are stable even though the swap replaces their DOM nodes. */
const seenStoryIds = new Set()

/* Stories index (Sprig): the swap replaces the whole listing after first
   paint, so its cascade columns land at the FOUC-guard opacity:0 with no
   ScrollTrigger to reveal them. Show what's already on screen instantly (no
   flash), then stagger only the new stories in with a soft rise. A wholly
   fresh result set (filter / search) animates in full. */
function revealSwappedStories(root) {
	root
		.querySelectorAll('.animate-reveal-cascade')
		.forEach((grid) => gsap.set(Array.from(grid.children), { autoAlpha: 1, y: 0 }))

	const articles = Array.from(root.querySelectorAll('article.story-feature'))
	if (articles.length === 0) return

	if (prefersReducedMotion) {
		articles.forEach((a) => seenStoryIds.add(a.dataset.storyId))
		return
	}

	const fresh = articles.filter((a) => !seenStoryIds.has(a.dataset.storyId))
	const entering = fresh.length === articles.length ? articles : fresh
	articles.forEach((a) => seenStoryIds.add(a.dataset.storyId))
	if (entering.length === 0) return

	gsap.set(entering, { autoAlpha: 0, y: 28 })
	// Mute the card hover-transition during the reveal so it can't fight GSAP.
	entering.forEach((a) => (a.style.transition = 'none'))
	gsap.to(entering, {
		autoAlpha: 1,
		y: 0,
		duration: 0.62,
		ease: 'power3.out',
		stagger: 0.08,
		clearProps: 'transform',
		onComplete: () => entering.forEach((a) => (a.style.transition = '')),
	})
}

document.addEventListener('DOMContentLoaded', () => {
	initReveals(document)
	document
		.querySelectorAll('article.story-feature[data-story-id]')
		.forEach((a) => seenStoryIds.add(a.dataset.storyId))
})

document.addEventListener('htmx:afterSettle', (event) => {
	const target = event.target
	if (!target || !target.querySelectorAll) return
	if (target.querySelector('[data-story-id]')) {
		revealSwappedStories(target)
	} else {
		initReveals(target, { immediate: true })
	}
})
